<script setup lang="ts">
import Column from 'primevue/column';
import { useRouter } from 'vue-router';
import type { DataTableRowClickEvent } from 'primevue/datatable';
import { storeToRefs } from 'pinia';
import { trans } from '@elements/translation';
import { currency } from '@elements/number-formatter';
import { getCompleteShorterReadableDate } from '@tools/date';
import UbiqTable from '@components/UbiqDataTable.vue';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';
import EmptyCardManager from './EmptyCardManager.vue';
import { useAccountStore } from '../store/account';
import CardEmptyTable from '../general/CardEmptyTable.vue';
import SubscriptionStatusTag from './components/SubscriptionStatusTag.vue';

const router = useRouter();
const accountStore = useAccountStore();
const { selectedSubscriptionData } = storeToRefs(useSubscriptionStore());

const emit = defineEmits<{(e: 'createSubscription'): void;
}>();

const goToSubscription = (e: DataTableRowClickEvent): void => {
  const { id } = e.data;
  if (!id) {
    return;
  }

  selectedSubscriptionData.value = e.data;
  router.push({ name: 'subscriptionDetails', params: { id } });
};
</script>

<template>
  <div>
    <UbiqTable class="contracts-table" scrollable @row-clicked="goToSubscription">
      <Column :header="trans('gestion.subscription.table.client')">
        <template #body="{ data }">
          {{ data.client?.legal_name ?? '-' }}
        </template>
      </Column>

      <Column :header="trans('gestion.subscription.table.amount')" class="column--currency column--bold">
        <template #body="{ data }">
          {{ currency(data.amount / 100) }}
        </template>
      </Column>

      <Column :header="trans('gestion.subscription.table.status')">
        <template #body="{ data }">
          <SubscriptionStatusTag v-if="data.status" :status="data.status.toLowerCase()" />
        </template>
      </Column>

      <Column :header="trans('gestion.subscription.table.address')">
        <template #body="{ data }">
          {{ data.address?.name ?? '-' }}
        </template>
      </Column>

      <Column :header="trans('gestion.subscription.table.officeOffers')">
        <template #body="{ data }">
          <template v-if="data.office_offers && data.office_offers.length > 0">
            <template v-for="(offer, index) in data.office_offers">
              {{ offer.name }}
              <template v-if="index as number < data.office_offers.length - 1">
                .
              </template>
            </template>
          </template>
          <template v-else>
            -
          </template>
        </template>
      </Column>

      <Column :header="trans('gestion.subscription.table.startDate')">
        <template #body="{ data }">
          {{ getCompleteShorterReadableDate(data.start_date.iso) }}
        </template>
      </Column>

      <Column :header="trans('gestion.subscription.table.endDate')" class="no-ellipsis">
        <template #body="{ data }">
          {{ data.end_date ? getCompleteShorterReadableDate(data.end_date?.iso) : '-' }}
        </template>
      </Column>

      <template #empty-state>
        <EmptyCardManager
          :status="accountStore.stripeAccountStatus"
        >
          <template #ready>
            <CardEmptyTable
              logo="ph_visit-planned_blue"
              :title="trans('gestion.subscription.empty.title')"
              :subtitle="trans('gestion.subscription.empty.subtitle')"
              :button="trans('messaging.decision.stripe.subscription.create')"
              :status="accountStore.stripeAccountStatus"
              @on-click-empty-table="emit('createSubscription')"
            />
          </template>
        </EmptyCardManager>
      </template>
    </UbiqTable>
  </div>
</template>
